import * as React from 'react';
import { QuestionRevision, BomColumnRevision, GeneralRevisionRequest, SupplierQuestionnaireRevisionRequest, BomRevisionRequest, SurveyHistoryEntryGranularRevisionRequest } from '../../Models/Surveys/SurveyDataModel'

export default class RevisionRequestManager {
    private _surveyRequestId: string;
    private _historyEntryId: string;
    private _granularRevisionRequest: SurveyHistoryEntryGranularRevisionRequest | null;

    // ===============================================================================

    public constructor(
        surveyRequestId: string,
        historyEntryId: string
    ) {
        this._surveyRequestId = surveyRequestId;
        this._historyEntryId = historyEntryId;
        this._granularRevisionRequest = null;
    }

    // ===============================================================================

    static fromOther(other: RevisionRequestManager) {
        return new RevisionRequestManager(other._surveyRequestId, other._historyEntryId);
    }

    // ===============================================================================

    public getGranularRevisionRequest() {
        return this._granularRevisionRequest;
    }

    // ===============================================================================

    private getLocalStorageKeyForSurveyHistoryEntryRevisionRequests() {
        return "survey-history-entry-revision-requests-" + this._historyEntryId;
    }

    // ===============================================================================

    public setGeneralEmailInstructions(message: string) {
        if (this._granularRevisionRequest === null) {
            throw new Error("no granular revision request loaded");
        }

        this._granularRevisionRequest.generalRevisionInstructions = message;

        this.saveSurveyHistoryEntryRevisionRequests();
    }

    // ===============================================================================

    public loadSurveyHistoryEntryRevisionRequests() {
        const storageKey = this.getLocalStorageKeyForSurveyHistoryEntryRevisionRequests();

        const json = localStorage.getItem(storageKey);

        if (json === null) {
            this._granularRevisionRequest = {
                generalRevisionInstructions: "",
                supplierQuestionnaireRevisionRequest: null,
                bomRevisionRequestTable: {},
                dateSentIso8601: null,
                dateRevisionResponseReceivedIso8601: null,
                sentTo: null
            };

            return;
        }

        const object = JSON.parse(json);

        //
        // Migrate legacy data
        //

        if (object["dateRevisionRequestRecievedIso8601"] !== undefined) {
            object["dateRevisionResponseReceivedIso8601"] = object["dateRevisionRequestRecievedIso8601"];
            delete object["dateRevisionRequestRecievedIso8601"];
        }

        if (object["supplierQuestionnaireRevisionRequest"] !== null) {
            if (object["supplierQuestionnaireRevisionRequest"]["dateRevisionRequestRecievedIso8601"] !== undefined) {
                object["supplierQuestionnaireRevisionRequest"]["dateRevisionResponseReceivedIso8601"] = object["supplierQuestionnaireRevisionRequest"]["dateRevisionRequestRecievedIso8601"];
                delete object["supplierQuestionnaireRevisionRequest"]["dateRevisionRequestRecievedIso8601"];
            }
        }

        for (let bomKey of Object.keys(object["bomRevisionRequestTable"])) {
            const bomRevisionRequest = object["bomRevisionRequestTable"][bomKey];

            if (bomRevisionRequest["dateRevisionRequestRecievedIso8601"] !== undefined) {
                bomRevisionRequest["dateRevisionResponseReceivedIso8601"] = bomRevisionRequest["dateRevisionRequestRecievedIso8601"];
                delete bomRevisionRequest["dateRevisionRequestRecievedIso8601"];
            }

            const newProperties = [
                "sustainabilityRevisionInstructions",
                "circularityRevisionInstructions"
            ];

            for (let newProperty of newProperties) {
                if (bomRevisionRequest[newProperty] === undefined) {
                    bomRevisionRequest[newProperty] = "";
                }
            }

            if (!bomRevisionRequest.dynamicRegulationRevisionInstructions) {
                bomRevisionRequest.dynamicRegulationRevisionInstructions = {};
            }
        }

        this._granularRevisionRequest = object as SurveyHistoryEntryGranularRevisionRequest;
    }

    // ===============================================================================

    private saveSurveyHistoryEntryRevisionRequests() {
        const json = JSON.stringify(this._granularRevisionRequest);

        const storageKey = this.getLocalStorageKeyForSurveyHistoryEntryRevisionRequests();

        localStorage.setItem(storageKey, json);
    }

    // ===============================================================================

    /**
     * @return whether all specific revision instructions in the given request are empty.
     */
    public static allSpecificRevisionInstructionsAreEmpty(bomRevisionRequest: BomRevisionRequest): boolean {
        return (bomRevisionRequest.bomQuestionnaireQuestionRevisions.length === 0) &&
            (bomRevisionRequest.attestationQuestionnaireQuestionRevisions.length === 0) &&
            (bomRevisionRequest.bomColumnRevisions.length === 0) &&
            !Object.values(bomRevisionRequest.dynamicRegulationRevisionInstructions).find(x => x.length > 0) &&
            (bomRevisionRequest.sustainabilityRevisionInstructions.length === 0) &&
            (bomRevisionRequest.circularityRevisionInstructions.length === 0);
    }

    // ===============================================================================

    public activeRequestIsEmpty() {
        if (this._granularRevisionRequest === null) {
            return true;
        }

        return (this._granularRevisionRequest.supplierQuestionnaireRevisionRequest === null) &&
            Object.keys(this._granularRevisionRequest.bomRevisionRequestTable).every(id => {
                const bomRevisionRequest = this._granularRevisionRequest!.bomRevisionRequestTable[id];

                return RevisionRequestManager.allSpecificRevisionInstructionsAreEmpty(bomRevisionRequest);
            });
    }

    // ===============================================================================

    public supplierQuestionnaireIsMarkedForRevision() {
        if (this._granularRevisionRequest === null) {
            return false;
        }

        return (this._granularRevisionRequest.supplierQuestionnaireRevisionRequest !== null) &&
            ((this._granularRevisionRequest.supplierQuestionnaireRevisionRequest.generalRevisionInstructions.length > 0) ||
                (this._granularRevisionRequest.supplierQuestionnaireRevisionRequest.supplierQuestionRevisions.length > 0));
    }

    // ===============================================================================

    public getSupplierQuestionnaireRevisionRequest() {
        return this._granularRevisionRequest?.supplierQuestionnaireRevisionRequest;
    }

    // ===============================================================================

    public bomIsMarkedForRevision(responseInventoryId: string) {
        if (this._granularRevisionRequest === null) {
            return false;
        }

        const bomRevisionRequest = this._granularRevisionRequest.bomRevisionRequestTable[responseInventoryId];

        if (!bomRevisionRequest) {
            return false;
        }

        return (bomRevisionRequest.generalRevisionInstructions.length > 0) ||
            (bomRevisionRequest.bomQuestionnaireQuestionRevisions.length > 0) ||
            (bomRevisionRequest.attestationQuestionnaireQuestionRevisions.length > 0) ||
            (bomRevisionRequest.bomColumnRevisions.length > 0);
    }

    // ===============================================================================

    public getBomRevisionRequest(responseInventoryId: string) {
        return this._granularRevisionRequest?.bomRevisionRequestTable[responseInventoryId] ?? null;
    }

    // ===============================================================================

    public copyBomRevisionToOtherBoms(sourceResponseInventoryId: string, destinationResponseInventoryIds: string[]) {
        if (this._granularRevisionRequest === null) {
            throw new Error("not initialized");
        }

        const sourceBomRevisionRequest = this._granularRevisionRequest.bomRevisionRequestTable[sourceResponseInventoryId];

        if (!sourceBomRevisionRequest) {
            return;
        }

        for (let destinationResponseInventoryId of destinationResponseInventoryIds) {
            this._granularRevisionRequest.bomRevisionRequestTable[destinationResponseInventoryId] = {
                ...sourceBomRevisionRequest,
                responseInventoryId: destinationResponseInventoryId
            };
        }

        this.saveSurveyHistoryEntryRevisionRequests();
    }

    public removeRevisionFromRequestTable(responseInventoryId:string) {
        delete this._granularRevisionRequest?.bomRevisionRequestTable[responseInventoryId];
        this.saveSurveyHistoryEntryRevisionRequests();
    }

    public getCurrentHistoryId() {
        return this._historyEntryId;
    }
}